export type DeepPartial<T> = T extends Date
  ? T
  : T extends readonly unknown[]
    ? T
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T;

export const ATOM_PATCH_DELETE_SENTINEL_KEY = "__atomPatchDelete";

/**
 * Framework-owned keys are prefixed with `__` and are never business data.
 *
 * Treat this as the single deny-by-default rule: anything matching it is
 * stripped at every boundary unless a boundary explicitly allowlists it.
 */
export const isInternalAtomKey = (key: string): boolean => key.startsWith("__");

/**
 * Internal metadata keys that live ON a versioned atom value but are NOT part
 * of its business data: `__history`/`__latestMeta` are owned by the persistence
 * service, and `__schemaVersion` is stamped onto `latest` by the persist proc
 * (`jsonb_set(latest,'{__schemaVersion}',…)`). They must be stripped from BOTH
 * sides of the old-vs-new diff so a plain business edit never emits a spurious
 * add/remove of a metadata key into the `changes` audit patch.
 */
export const VERSIONED_META_KEYS = [
  "__history",
  "__latestMeta",
  "__schemaVersion",
] as const;

/**
 * The only internal keys allowed to cross the wire outbound. `__history` is
 * additionally gated behind the `includeHistory` read parameter; everything
 * else matching {@link isInternalAtomKey} is dropped before serialization.
 */
export const WIRE_META_ALLOWLIST = ["__latestMeta", "__history"] as const;

export const ATOM_PATCH_DELETE_SENTINEL = {
  [ATOM_PATCH_DELETE_SENTINEL_KEY]: true,
} as const;

export type AtomPatchDeleteSentinel = typeof ATOM_PATCH_DELETE_SENTINEL;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
};

export function isAtomPatchDeleteSentinel(
  value: unknown,
): value is AtomPatchDeleteSentinel {
  return (
    isPlainObject(value) &&
    Object.keys(value).length === 1 &&
    value[ATOM_PATCH_DELETE_SENTINEL_KEY] === true
  );
}

/**
 * Recursively drop every framework-owned key from a value.
 *
 * Returns the SAME reference when nothing was removed, so already-clean values
 * cost no allocation. Callers must therefore treat the result as read-only
 * unless they passed in a copy they own.
 */
export function stripAtomMetadata<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const stripped = stripAtomMetadata(entry);
      if (stripped !== entry) {
        changed = true;
      }
      return stripped;
    });
    return changed ? (next as T) : value;
  }
  if (!isPlainObject(value)) {
    return value;
  }

  let changed = false;
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isInternalAtomKey(key)) {
      changed = true;
      continue;
    }
    const nested = stripAtomMetadata(entry);
    if (nested !== entry) {
      changed = true;
    }
    result[key] = nested;
  }
  return changed ? (result as T) : value;
}

/**
 * Options for every outbound (wire-facing) filter.
 *
 * `includeHistory` is deliberately REQUIRED. Each outbound path must state what
 * the caller asked for; four separate review rounds on this branch were caused
 * by a call site silently defaulting it, so the compiler now forces the choice.
 */
export interface WireFilterOptions {
  includeHistory: boolean;
}

/** Keys the outbound policy permits at the TOP LEVEL of an atom value. */
const isAllowedAtTopLevel =
  ({ includeHistory }: WireFilterOptions) =>
  (key: string): boolean =>
    key === "__latestMeta" || (key === "__history" && includeHistory);

/**
 * Recursively drop framework-owned keys from a value on its way OUT to a client.
 *
 * Differs from {@link stripAtomMetadata} only at the top level, where the atom's
 * own metadata is legitimate: `__latestMeta` always survives and `__history`
 * survives when explicitly requested. Every nested level is stripped outright —
 * nested internals are never business data, and because inbound writes go
 * through the fully-recursive {@link stripAtomMetadata}, anything left nested on
 * the way out could never round-trip. That asymmetry is what wedges MagicForm
 * drafts into a permanent phantom diff, so keep the two directions symmetric.
 *
 * Returns the SAME reference when nothing was removed, so the common (clean)
 * case allocates nothing on hot subscription paths and callers can rely on
 * identity to detect a no-op.
 */
export function stripInternalAtomFieldsForWire<T>(
  value: T,
  options: WireFilterOptions,
): T {
  // Arrays are a legitimate payload shape (a bare list of atoms). Recurse rather
  // than passing them through — treating "not a plain object" as "nothing to do"
  // silently exempted every array-shaped payload from the policy.
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const stripped = stripInternalAtomFieldsForWire(entry, options);
      if (stripped !== entry) {
        changed = true;
      }
      return stripped;
    });
    return changed ? (next as T) : value;
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const isAllowed = isAllowedAtTopLevel(options);

  let changed = false;
  const result: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(value)) {
    if (isInternalAtomKey(key) && !isAllowed(key)) {
      changed = true;
      continue;
    }
    // Allowlisted metadata is framework-owned and passed through untouched;
    // everything else is business data whose interior must be stripped.
    const nested = isAllowed(key) ? entry : stripAtomMetadata(entry);
    if (nested !== entry) {
      changed = true;
    }
    result[key] = nested;
  }

  return changed ? (result as T) : value;
}

/**
 * Apply the outbound internal-field policy to raw JSON-Patch ops.
 *
 * `__partialUpdate` atoms ship their patches to the client without going
 * through the transform pipeline, so the policy has to be enforced here.
 *
 * Every path segment is checked, not just the first: an op like
 * `add /entries/0/__schemaVersion` targets a perfectly ordinary `entries` array
 * yet still carries an internal key, and a primitive value cannot be scrubbed
 * by stripping. Such ops are dropped outright — the client never received those
 * keys, so the op would either leak an internal value or fail to apply against
 * state where the field was already stripped.
 *
 * Allowlisting matches {@link stripInternalAtomFieldsForWire}: `__latestMeta`
 * always survives and `__history` survives when the subscriber asked for it —
 * and, as there, an allowlisted subtree is passed through UNTOUCHED. Stripping
 * inside it would make an `/__history/-` op deliver different data than the same
 * changelog delivered as a full value.
 */
export function filterInternalFieldPatches<
  PatchType extends { path: string; from?: string; value?: unknown },
>(patches: PatchType[], options: WireFilterOptions): PatchType[] {
  const isAllowed = isAllowedAtTopLevel(options);

  /** True when a JSON Pointer targets, or reaches through, a denied key. */
  const pointerTouchesDeniedKey = (pointer: string): boolean => {
    const segments = pointer.split("/").slice(1);
    const [first] = segments;
    if (first !== undefined && isAllowed(first)) {
      // Allowlisted subtree — everything below it travels with it.
      return false;
    }
    return segments.some((segment) => isInternalAtomKey(segment));
  };

  /** Which strip applies to the value an op carries, given where it lands. */
  const scrubOpValue = (path: string, value: unknown): unknown => {
    // Only the empty pointer addresses the whole document; "/" addresses the
    // key "". A root op carries the whole atom, so the top-level allowlist
    // applies.
    if (path === "") {
      return stripInternalAtomFieldsForWire(value, options);
    }
    // An allowlisted subtree travels untouched, matching the value filter.
    const [firstSegment] = path.split("/").slice(1);
    if (firstSegment !== undefined && isAllowed(firstSegment)) {
      return value;
    }
    // Anything else is business data, stripped outright.
    return stripAtomMetadata(value);
  };

  return patches
    .filter((patch) => {
      // `move`/`copy` carry a second pointer in `from`. Checking only `path`
      // let `{op:"move", from:"/__schemaVersion", path:"/leaked"}` relocate an
      // internal value into business data, and `{op:"copy", from:"/__history/0"}`
      // additionally throws OPERATION_FROM_UNRESOLVABLE on a client whose copy
      // of that key was stripped — killing the subscription.
      if (patch.from !== undefined && pointerTouchesDeniedKey(patch.from)) {
        return false;
      }
      return !pointerTouchesDeniedKey(patch.path);
    })
    .map((patch) => {
      if (!("value" in patch)) {
        return patch;
      }
      const value = scrubOpValue(patch.path, patch.value);
      return value === patch.value ? patch : { ...patch, value };
    });
}

export function serializePatchForTransport<T>(value: T): T {
  if (value === undefined) {
    return ATOM_PATCH_DELETE_SENTINEL as T;
  }
  if (value === null || value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => serializePatchForTransport(entry)) as T;
  }
  if (isAtomPatchDeleteSentinel(value)) {
    return value;
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !isInternalAtomKey(key))
        .map(([key, entry]) => [key, serializePatchForTransport(entry)]),
    ) as T;
  }
  return value;
}
